// FILE: apps/admin/src/pages/AuditLogs.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Immutable Admin Audit Log explorer with human-readable Russian action titles, actor/entity filters, and before/after metadata inspection.
//   SCOPE: AuditLogs component.
//   DEPENDS: ../api.js, ../audit-catalog.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   AuditLogs - Admin Audit Log viewer page
// END_MODULE_MAP

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api, dt } from '../api.js';
import { AUDIT_ACTION_CATALOG, describeAuditAction } from '../audit-catalog.js';

interface AuditRow {
  id: string;
  actorType: string;
  actorTelegramId: number | null;
  actorUsername: string | null;
  actorRole: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  privileged: boolean;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

// START_CONTRACT: AuditLogs
//   PURPOSE: Render the filterable Admin Audit Log table with Russian action descriptions.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries GET /admin/audit
//   LINKS: M-ADMIN
// END_CONTRACT: AuditLogs
export function AuditLogs() {
  // START_BLOCK_AUDIT_LOGS
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit', actionFilter, entityFilter, actorFilter],
    queryFn: () =>
      api.get<{ items: AuditRow[] }>(
        `/admin/audit?action=${encodeURIComponent(actionFilter)}&entityType=${encodeURIComponent(entityFilter)}&actorTelegramId=${encodeURIComponent(actorFilter)}&limit=150`,
      ),
    refetchInterval: 15000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Журнал аудита (Audit Trail)</h1>
          <div className="page-subtitle">
            Неизменяемый реестр всех действий операторов, RBAC-отказов и Step-Up подтверждений
          </div>
        </div>
      </div>

      <div className="toolbar">
        <select
          style={{ maxWidth: '100%', minWidth: 0 }}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          <option value="">Все действия</option>
          {Object.values(AUDIT_ACTION_CATALOG).map((m) => (
            <option key={m.action} value={m.action}>
              {m.title} ({m.action})
            </option>
          ))}
        </select>

        <input
          placeholder="Тип объекта (user, order, banner…)"
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
        />

        <input
          placeholder="Telegram ID оператора…"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="muted">Загрузка журнала аудита…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Действие</th>
                  <th>Оператор</th>
                  <th>Объект</th>
                  <th>Причина / Детали</th>
                  <th>Время</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((log) => {
                  const meta = describeAuditAction(log.action);
                  return (
                    <tr key={log.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{meta.title}</div>
                        <div className="mono muted" style={{ fontSize: 11 }}>{log.action}</div>
                      </td>
                      <td>
                        <div className="mono">
                          {log.actorUsername
                            ? `@${log.actorUsername}`
                            : log.actorTelegramId
                              ? `#${log.actorTelegramId}`
                              : log.actorType}
                        </div>
                        {log.actorRole && (
                          <span className="chip info" style={{ marginTop: 2 }}>{log.actorRole}</span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {log.entityType}:{log.entityId ? log.entityId.slice(0, 10) : '—'}
                      </td>
                      <td style={{ maxWidth: 360 }}>
                        <div>{log.reason || '—'}</div>
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="mono muted" style={{ fontSize: 11, marginTop: 4, wordBreak: 'break-all' }}>
                            {JSON.stringify(log.metadata).slice(0, 120)}
                          </div>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{dt(log.createdAt)}</td>
                    </tr>
                  );
                })}
                {!data?.items?.length && (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Записей аудита по выбранным фильтрам нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
  // END_BLOCK_AUDIT_LOGS
}
